import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { cn } from '@/lib/utils/cn';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useReportScheduleLogs } from '../api';
import type { ReportScheduleLogRow } from '../types';

export function ReportSchedulingLogsPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const scheduleId = Number(idParam);
  const query = useReportScheduleLogs(scheduleId);
  const rows = query.data ?? [];

  const columns = useMemo<ColumnDef<ReportScheduleLogRow>[]>(
    () => [
      {
        accessorKey: 'reportScheduleId',
        header: 'Schedule ID',
        size: 120,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.reportScheduleId ?? '—'}
          </span>
        ),
      },
      { accessorKey: 'frequency', header: 'Frequency', size: 110 },
      { accessorKey: 'status', header: 'Status', size: 130 },
      {
        accessorKey: 'errorMessage',
        header: 'Error',
        cell: ({ row }) =>
          row.original.errorMessage ? (
            <span className="text-xs text-destructive">{row.original.errorMessage}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      { accessorKey: 'sentAt', header: 'Sent', size: 180 },
      { accessorKey: 'createdAt', header: 'Created', size: 180 },
    ],
    [],
  );

  return (
    <FillPage>
      <PageHeader
        title="Report Schedule Logs"
        description={`Run history for schedule #${scheduleId}.`}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              aria-label="Refresh"
              title="Refresh"
            >
              <RefreshCw className={cn('h-4 w-4', query.isFetching && 'animate-spin')} />
            </Button>
            <Button asChild variant="outline">
              <Link to="/report-scheduling">
                <ArrowLeft className="h-4 w-4" />
                Back to schedules
              </Link>
            </Button>
          </div>
        }
      />

      <DataTable<ReportScheduleLogRow, unknown>
        tableId="reports:schedule:logs:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search logs…"
        emptyTitle="No runs yet"
        emptyDescription="Logs will appear here once this schedule has fired at least once."
        getRowId={(row, i) => `${row.id ?? `r${i}`}`}
        exportFilename={`schedule-${scheduleId}-logs.csv`}
        pageSize={10}
      />
    </FillPage>
  );
}
